/**
 * The outreach playbook — one bilingual template per pipeline stage.
 *
 * These are product copy, not code. Treat an edit here the way you would treat
 * an edit to the app's onboarding text.
 *
 * ## The voice
 *
 * Direct, specific, and short enough to read on a phone between two tours.
 * Every template says who is writing and why *them*, explains the product in
 * one concrete sentence rather than three abstract ones, and ends with a single
 * small ask.
 *
 * The ask names a *shape* — ten minutes, morning or afternoon, whichever day —
 * not a slot. Two named times only work in the week the template was written,
 * and they ask a guide to fit a calendar they do not keep: these are people
 * whose week is decided by weather and who are out on the hill, not at a desk
 * in admin mode. Offering to work around that is both true and the more
 * persuasive thing to say.
 *
 * No superlatives, no "revolutionary", no paragraph that could have been sent
 * to any company in any industry.
 *
 * ## Length is a hard constraint, not a preference
 *
 * A `mailto:` URL carries the whole body percent-encoded, and Windows truncates
 * silently somewhere around 2,000 characters. Norwegian encodes at roughly 1.45
 * characters per character once æøå and em dashes are escaped, so the budget
 * works out at **under 900 raw characters per body, signature included** — that
 * keeps the finished URL comfortably below 1,500.
 *
 * `scripts/pushTemplates.ts` refuses to publish a body that breaks it. The
 * limit is not aesthetic: over it, the reader gets an email with no body at all.
 *
 * ## The signature appears exactly once
 *
 * `SIGNATURE` is appended to each body and nowhere else — no template writes
 * its own sign-off. It is deliberately two lines: the sender's address is
 * already in the From field, and most mail clients add their own block
 * signature underneath, so anything longer here reads as the same person
 * signing twice.
 *
 * ## Merge fields
 *
 * `[Navn]`, `[Selskap]`, `[Avsender]` and `[Tittel]` are filled in from the
 * lead's first contact and the signed-in rep — see `features/leads/merge.ts`.
 * A token whose value is missing stays visible in the preview so it gets caught
 * before sending, except in the signature, where an absent title removes its
 * line rather than leaving a hole.
 *
 * Anything else in brackets — `[du/dere]` — is a choice for the rep, left alone
 * on purpose.
 */
import type { EmailTemplate, TemplateKey } from '../../types/crm'

/**
 * The one sign-off, used by every template. Two lines: the address is in the
 * From field, and the client usually adds its own signature below.
 */
const SIGNATURE = `
Beste hilsen,
[Avsender]
[Tittel]`

export const DEFAULT_TEMPLATES: Record<TemplateKey, EmailTemplate> = {
  archived: {
    title: 'Archived — not in the pipeline',
    guidance:
      'Somebody looked at this during verification and decided against it. It is kept rather than deleted so the next import does not resurrect it as a fresh lead. Move it back to Prospect if that judgement was wrong.',
    subjectNo: '',
    bodyNo: '',
    subjectEn: '',
    bodyEn: '',
  },

  'do-not-contact': {
    title: 'Do not contact this company',
    guidance:
      'Something on this lead means outreach would be harmful — read the banner at the top before anything else. Usually a fatal avalanche involving their guides or clients. There is no template and no sequence: leave it alone until a human decides otherwise, and never let a bulk action pick it up.',
    subjectNo: '',
    bodyNo: '',
    subjectEn: '',
    bodyEn: '',
  },

  unverified: {
    title: 'Verify this lead before contacting anyone',
    guidance:
      'This lead came in without enough to work with — no contact address, no website, or nothing to tie it to a company. Find the missing pieces, or archive it. There is no outreach template because there is nobody to send it to yet: an email to a guessed address is worse than no email.',
    subjectNo: '',
    bodyNo: '',
    subjectEn: '',
    bodyEn: '',
  },

  prospect_aware: {
    title: 'First contact — they already know Skida',
    guidance:
      'Someone at the company already uses the consumer app, so skip explaining what Skida is. Straight to what PRO adds, then one small ask: ten minutes, and let them pick the day. The seat including skida.app Premium lands hardest here, because they may already be paying for it — it makes the real price of PRO lower than the number suggests. Naming two slots in a template only works the week it was written, and it puts the scheduling burden on somebody whose week is decided by weather.',
    subjectNo: 'Skida PRO for [Selskap] — ti minutter når det passer deg?',
    bodyNo: `Hei [Navn],

Siden Skida allerede er kjent hos dere, gjør jeg denne kort.

Vi har lansert Skida PRO for guideoperatører: offisielle skredvarsler lagt rett oppå deres egne ruteplaner i HD 3D-kart. Kvalitetssikringen blir enkel å dokumentere, og gjestebriefen blir visuell — koblet til Skida-appen gjestene allerede bruker.

Verdt å vite: et PRO-sete inkluderer Premium i skida.app for guiden. Betaler noen hos dere for Premium privat i dag, ligger det allerede inne i prisen.

Vi rigger porteføljen foran sesongen 2026/2027. Har du ti minutter på video en gang de neste par ukene? Formiddag eller ettermiddag, den dagen som passer deg — jeg regner med at de fleste dagene dine er ute og ikke på kontoret, så jeg legger meg etter din kalender.
${SIGNATURE}`,
    subjectEn: 'Skida PRO for [Selskap] — ten minutes when it suits?',
    bodyEn: `Hi [Navn],

Since Skida is already familiar to your team, I will keep this short.

We have launched Skida PRO for guiding operators: the official avalanche forecast laid directly over your own route plans in HD 3D maps. Quality assurance becomes easy to document, and the guest briefing becomes visual — connected to the Skida app they already use.

Worth knowing: a PRO seat includes Premium in the skida.app for the guide. If anyone on your team pays for Premium privately today, it is already covered.

We are building the portfolio ahead of the 2026/2027 season. Could you spare ten minutes on video sometime in the next couple of weeks? Morning or afternoon, whichever day suits — most of your days are out rather than at a desk, so I will work around you.
${SIGNATURE}`,
  },

  prospect_unaware: {
    title: 'First contact — new to Skida',
    guidance:
      'Nobody here appears to know Skida yet, so one sentence establishes what it is before the pitch. Same single ask at the end — ten minutes, on their terms.',
    subjectNo: 'Skredvarsel rett i ruteplanen — Skida PRO for [Selskap]',
    bodyNo: `Hei [Navn],

Jeg kontakter deg fordi du opererer som profesjonell guide, og vi vil gjøre hverdagen din i vinterfjellet enklere og tryggere.

Skida er appen mange norske topptur-folk bruker til ruteplanlegging og skredvurdering. Skida PRO er den profesjonelle versjonen: offisielle skredvarsler lagt rett oppå dine egne ruteplaner i HD 3D-kart. Det gjør kvalitetssikringen enkel å dokumentere, og lar deg briefe gjestene visuelt før turen. Hvert PRO-sete inkluderer også Premium i appen for guiden.

Vi rigger porteføljen foran sesongen 2026/2027. Har du ti minutter på video en gang de neste par ukene? Formiddag eller ettermiddag, den dagen som passer — jeg vet dagene sjelden er på kontoret, så si hva som fungerer for deg.
${SIGNATURE}`,
    subjectEn: 'The avalanche forecast, inside your route plan — Skida PRO',
    bodyEn: `Hi [Navn],

I am getting in touch because you work as a professional guide, and we want to make your days in the winter mountains simpler and safer.

Skida is the app a great many backcountry skiers use for route planning and avalanche assessment. Skida PRO is the professional version: the official forecast laid directly over your own route plans in HD 3D maps. That makes quality assurance easy to document, and lets you brief guests visually before the tour. Every PRO seat also includes Premium in the app for the guide.

We are building the portfolio ahead of the 2026/2027 season. Could you spare ten minutes on video in the next couple of weeks? Morning or afternoon, whichever day works — I know your days are rarely at a desk, so tell me what suits.
${SIGNATURE}`,
  },

  contacted: {
    title: 'Follow up — no reply yet',
    guidance:
      'One short nudge with a reason to open it, and make it easy to say no — a clear out tells you where you stand, and beats silence.',
    subjectNo: 'Kort oppfølging — Skida PRO før sesongen',
    bodyNo: `Hei [Navn],

Jeg sendte deg en linje om Skida PRO for en stund siden, og vet det er travelt.

Kjernen: offisielle skredvarsler oppå dine egne ruteplaner i HD 3D-kart, så kvalitetssikringen blir enkel å dokumentere og gjestebriefen visuell. Setet inkluderer Premium i appen for guiden.

Ti minutter på video holder, formiddag eller ettermiddag den dagen som passer deg. Er det feil tidspunkt i sesongen, si fra — så tar jeg kontakt igjen når det er roligere.
${SIGNATURE}`,
    subjectEn: 'Quick follow-up — Skida PRO before the season',
    bodyEn: `Hi [Navn],

I sent you a line about Skida PRO a while ago, and I know how busy it gets.

The core of it: the official avalanche forecast over your own route plans in HD 3D maps, so quality assurance is easy to document and the guest briefing becomes visual. The seat includes Premium in the app for the guide.

Ten minutes on video is enough, morning or afternoon on whichever day suits you. If this is the wrong point in the season, say so and I will come back when things are quieter.
${SIGNATURE}`,
  },

  proposal: {
    title: 'They are in the product — help them get value out of it',
    guidance:
      'Somebody here has signed in, so this is no longer a pitch. Offer to set the first routes up with them; a trial nobody has loaded a route into will not convert, whatever you send.',
    subjectNo: 'Skal jeg sette opp de første rutene i Skida PRO?',
    bodyNo: `Hei [Navn],

Fint å se at [Selskap] er i gang i Skida PRO.

Det som avgjør om verktøyet blir brukt, er de første rutene. Vil du, setter jeg opp et par av deres faste turer sammen med deg — med skredvarsel, bratthet og utløpssoner på plass — så ser dere det med ekte terreng.

Ti minutter på video holder — velg en dag og et tidspunkt som passer deg, formiddag eller ettermiddag. Si også fra hvis noen på laget mangler tilgang.
${SIGNATURE}`,
    subjectEn: 'Shall I set up your first routes in Skida PRO?',
    bodyEn: `Hi [Navn],

Good to see [Selskap] up and running in Skida PRO.

What decides whether the tool gets used is the first few routes. If you like, I will set up a couple of your regular tours with you — avalanche forecast, slope angle and runout in place — so you see it with real terrain.

Ten minutes on video is enough — pick a day and a time that suits, morning or afternoon. Do say if anyone on the team is missing access.
${SIGNATURE}`,
  },

  won: {
    title: 'Won — make sure the whole team is actually on it',
    guidance:
      'The deal is signed; the risk now is that two guides use it and the rest never log in. Ask who is missing, and offer the walkthrough to the team rather than the buyer. Telling the guides their seat carries personal Premium is the cheapest way to get them to log in — it is worth something to them, not just to the company.',
    subjectNo: 'Velkommen om bord — er hele laget på plass?',
    bodyNo: `Hei [Navn],

Takk for at [Selskap] er med på Skida PRO.

Én ting er verdt å ta tidlig: at alle guidene er inne, ikke bare de som satte det opp. Send meg navnene på dem som mangler, så aktiverer jeg setene. Verdt å si til laget at setet også gir dem Premium i skida.app privat — de som betaler for det i dag kan si opp.

Jeg tar gjerne en kort gjennomgang for hele laget før sesongen — 20 minutter på video, når det passer dere å samle folk. Da har alle sett de samme kartene og de samme rutinene.
${SIGNATURE}`,
    subjectEn: 'Welcome aboard — is the whole team set up?',
    bodyEn: `Hi [Navn],

Thank you for bringing [Selskap] onto Skida PRO.

One thing is worth doing early: making sure every guide is in, not just the people who set it up. Send me the names of anyone missing and I will activate their seats. Worth telling the team the seat also gives them Premium in the skida.app personally — anyone paying for that today can cancel it.

I am happy to run a short walkthrough for the whole team before the season — 20 minutes on video, whenever you can get people together. Then everyone has seen the same maps and the same routines.
${SIGNATURE}`,
  },

  lost: {
    title: 'Lost — leave the door open, and find out why',
    guidance:
      'Ask one question and make it easy to answer. The reason is worth more than a re-pitch: it is the only thing that makes the next twenty conversations better.',
    subjectNo: 'Takk for praten — én kjapp ting',
    bodyNo: `Hei [Navn],

Takk for at du tok deg tid. Helt fint at Skida PRO ikke passer for [Selskap] nå.

Én ting, hvis du har et halvt minutt: hva gjorde utslaget? Pris, timing, eller at verktøyet ikke traff måten dere jobber på? Det er den typen svar som gjør at vi bygger riktigere.

Jeg tar kontakt igjen før sesongen 2027/2028 med mindre du sier noe annet. Lykke til i vinter.
${SIGNATURE}`,
    subjectEn: 'Thanks for the conversation — one quick thing',
    bodyEn: `Hi [Navn],

Thank you for taking the time. It is entirely fine that Skida PRO is not the right fit for [Selskap] right now.

One thing, if you have half a minute: what tipped it? Price, timing, or the tool not matching how you work? That is the kind of answer that makes us build the right things.

I will get back in touch before the 2027/2028 season unless you say otherwise. Have a good winter.
${SIGNATURE}`,
  },
}

/** What a body may cost, raw. See the module note for how it was derived. */
export const MAX_BODY_CHARS = 900

/** The sign-off, so a linter can check it appears exactly once per body. */
export const SIGNATURE_MARKER = 'Beste hilsen,'
